/**
 * Centralized formatting utilities for the CPG Simulator
 *
 * Key rules:
 * - Always use comma as thousands separator (1,000,000)
 * - English: "B" = Billion (10^9)
 * - Spanish: "Miles de MM" for 10^9 (NEVER use "B" - Spanish "billón" = 10^12)
 * - Currency code always appears after the number
 */

export type Locale = 'en' | 'es';

export interface FormatOptions {
  locale?: Locale;
  decimals?: number;
}

export interface CurrencyFormatOptions extends FormatOptions {
  currency?: string;
  showCurrencyCode?: boolean;
}

/**
 * Format a number with comma separators
 * @example formatNumber(1234567) => "1,234,567"
 */
export function formatNumber(value: number, options: FormatOptions = {}): string {
  const { decimals = 0 } = options;
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format large numbers with appropriate suffix based on locale
 * - English: K (thousands), M (millions), B (billions)
 * - Spanish: K (miles), MM (millones), Miles de MM (miles de millones)
 *
 * IMPORTANT: Spanish "billón" = 10^12, NOT 10^9 like US billion
 *
 * @example formatLargeNumber(2100000000, 'en') => "2.1B"
 * @example formatLargeNumber(2100000000, 'es') => "2.1 Miles de MM"
 */
export function formatLargeNumber(value: number, locale: Locale = 'en'): string {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1e12) {
    // Trillions (English) / Billones (Spanish)
    const formatted = (absValue / 1e12).toFixed(1);
    return locale === 'es'
      ? `${sign}${formatted} Billones`
      : `${sign}${formatted}T`;
  }

  if (absValue >= 1e9) {
    // Billions (English) / Miles de millones (Spanish)
    const formatted = (absValue / 1e9).toFixed(1);
    return locale === 'es'
      ? `${sign}${formatted} Miles de MM`
      : `${sign}${formatted}B`;
  }

  if (absValue >= 1e6) {
    // Millions
    const formatted = (absValue / 1e6).toFixed(1);
    return locale === 'es'
      ? `${sign}${formatted} MM`
      : `${sign}${formatted}M`;
  }

  if (absValue >= 1e3) {
    // Thousands
    const formatted = (absValue / 1e3).toFixed(1);
    return locale === 'es'
      ? `${sign}${formatted} mil`
      : `${sign}${formatted}K`;
  }

  return `${sign}${absValue.toFixed(0)}`;
}

/**
 * Format currency with locale-aware large number formatting
 * Currency code appears after the number
 *
 * @example formatCurrency(2100000000, 'COP', 'en') => "2.1B COP"
 * @example formatCurrency(2100000000, 'COP', 'es') => "2.1 Miles de MM COP"
 */
export function formatCurrency(
  value: number,
  currency: string = 'USD',
  locale: Locale = 'en',
  options: { showCurrencyCode?: boolean } = {}
): string {
  const { showCurrencyCode = true } = options;
  const formattedValue = formatLargeNumber(value, locale);
  return showCurrencyCode ? `${formattedValue} ${currency}` : formattedValue;
}

/**
 * Format a full currency value with comma separators (no abbreviation)
 *
 * @example formatFullCurrency(2100000000, 'COP') => "2,100,000,000 COP"
 */
export function formatFullCurrency(
  value: number,
  currency: string = 'USD',
  decimals: number = 0
): string {
  return `${formatNumber(value, { decimals })} ${currency}`;
}

/**
 * Format percentage with optional decimal places
 *
 * @example formatPercent(0.234) => "23.4%"
 * @example formatPercent(0.234, 0) => "23%"
 */
export function formatPercent(value: number, decimals: number = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Format a trend value with +/- prefix
 * Returns the formatted string and a color class
 *
 * @example formatTrend(0.023) => { text: "+2.3%", colorClass: "text-success-400", isPositive: true }
 * @example formatTrend(-0.015) => { text: "-1.5%", colorClass: "text-danger-400", isPositive: false }
 */
export function formatTrend(value: number, decimals: number = 1): {
  text: string;
  colorClass: string;
  isPositive: boolean;
  arrow: string;
} {
  const isPositive = value >= 0;
  const prefix = isPositive ? '+' : '';
  const text = `${prefix}${(value * 100).toFixed(decimals)}%`;
  const colorClass = isPositive ? 'text-success-400' : 'text-danger-400';
  const arrow = isPositive ? '▲' : '▼';

  return { text, colorClass, isPositive, arrow };
}

/**
 * Format trend with arrow indicator
 *
 * @example formatTrendWithArrow(0.023) => "+2.3% ▲"
 */
export function formatTrendWithArrow(value: number, decimals: number = 1): string {
  const { text, arrow } = formatTrend(value, decimals);
  return `${text} ${arrow}`;
}

/**
 * Get the locale-appropriate label for share metrics
 */
export function getShareLabel(type: 'volume' | 'revenue', locale: Locale = 'en'): string {
  const labels = {
    volume: { en: 'Share (Volume)', es: 'Participación (Volumen)' },
    revenue: { en: 'Share (Revenue)', es: 'Participación (Valor)' },
  };
  return labels[type][locale];
}

/**
 * Format a date range for chart axis labels
 * Ensures labels don't overlap by using short format
 *
 * @example formatChartDate('2024-Q1') => "'24Q1"
 */
export function formatChartDate(dateStr: string): string {
  // Handle quarter format: "2024-Q1" => "'24Q1"
  const quarterMatch = dateStr.match(/^(\d{4})-Q(\d)$/);
  if (quarterMatch) {
    const year = quarterMatch[1].slice(-2);
    const quarter = quarterMatch[2];
    return `'${year}Q${quarter}`;
  }

  // Handle year-month format: "2024-01" => "Jan'24"
  const monthMatch = dateStr.match(/^(\d{4})-(\d{2})$/);
  if (monthMatch) {
    const year = monthMatch[1].slice(-2);
    const month = parseInt(monthMatch[2], 10);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[month - 1]}'${year}`;
  }

  // Return as-is if no match
  return dateStr;
}

/**
 * Truncate label to prevent axis overlap
 */
export function truncateLabel(label: string, maxLength: number = 10): string {
  if (label.length <= maxLength) return label;
  return `${label.slice(0, maxLength - 1)}…`;
}
